import { useQuery } from '@tanstack/react-query';

import { ActionItem, SessionStatus } from '~/services/types';
import { useHttpClient } from '~/hooks/useHttpClient';
import { determineRetryAndMaybeLog } from '~/utils/errors';
import { createLogger } from '~/utils/logging';

import { useReviewParams } from './useReviewParams';

const log = createLogger('useReviewActionItemsQuery');

export const QUERY_KEY = 'reviewActionItems';

export type ReviewActionItemsResponse = {
  actionItems: ActionItem[];
  session?: {
    status: SessionStatus;
  };
};

export function useReviewActionItems({ date, periodId }: { date: Date; periodId: string }) {
  const httpClient = useHttpClient();
  const params = useReviewParams({ date, periodId });

  const paramsQueryStr = params.toQueryString();

  const { data, error, status } = useQuery<null | ReviewActionItemsResponse>({
    queryKey: [QUERY_KEY, paramsQueryStr],
    queryFn: async () => {
      log.debug('Fetching review action items', {
        day: params.day,
        periodId: params.periodId,
        timeZone: params.timeZone ?? 'unknown',
      });
      const { actionItems, session } = await httpClient.get<ReviewActionItemsResponse>(
        `/review/getActionItems?${paramsQueryStr}`,
      );
      log.info('Fetched action items successfully', {
        actionItemCount: actionItems.length,
      });
      return { actionItems, session };
    },
    retry: (failureCount, error) =>
      determineRetryAndMaybeLog({ operation: 'fetch review action items', failureCount, error, log }),
    enabled: httpClient.isAuthenticated,
  });

  return { data, error, status };
}
